import React from "react";
import { FileText, Save, Snowflake, Loader2, Settings, GitBranch, Users, AlertTriangle, Trash2 } from "lucide-react";
import StatusBadge from "../StatusBadge";
import MerchantDetail from "./MerchantDetail";

const KYC_LABELS = { pan: "PAN Card", gst: "GST Certificate", business: "Business Proof" };

function Table({ cols, children, testid }) {
  return (
    <div className="ledger-card overflow-hidden rounded-2xl">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
            <tr>{cols.map((c, i) => <th key={i} className="p-4 text-left">{c}</th>)}</tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent, testid }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      <p data-testid={testid} className={`mt-1 font-heading text-lg font-bold ${accent ? "text-emerald-400" : "text-slate-100"}`}>{value ?? "—"}</p>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-widest text-slate-500">{label}</label>
      <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
    </div>
  );
}

// Renders the admin tabs that are still inline (non-delegated). Returns null for delegated tabs.
export default function InlineTabs({ d }) {
  const { tab, isSuper } = d;

  if (tab === "transactions") return (
    <Table testid="admin-transactions-table" cols={["Date", "Merchant", "Mobile", "Cashback", "Status", ""]}>
      {d.txns.map((t) => (
        <tr key={t.txn_id} className="border-t border-slate-800/60">
          <td className="p-4 text-slate-400">{new Date(t.created_at).toLocaleString("en-IN")}</td>
          <td className="p-4 font-mono text-xs">{t.merchant_id}</td>
          <td className="p-4">{t.customer_mobile}</td>
          <td className="p-4 font-mono text-emerald-400">₹{(t.amount_paise / 100).toLocaleString("en-IN")}</td>
          <td className="p-4"><StatusBadge status={t.status} /></td>
          <td className="p-4">{["PENDING", "AVAILABLE"].includes(t.status) && <button data-testid={`reverse-${t.txn_id}`} onClick={() => d.reverse(t.txn_id)} className="rounded-full border border-rose-500/40 px-3 py-1 text-xs text-rose-300 hover:bg-rose-500/10">Reverse</button>}</td>
        </tr>
      ))}
    </Table>
  );

  if (tab === "review") return (
    <Table testid="admin-review-table" cols={["Date", "Mobile", "Amount", "Status", ""]}>
      {d.review.transactions.map((t) => (
        <tr key={t.txn_id} className="border-t border-slate-800/60">
          <td className="p-4 text-slate-400">{new Date(t.created_at).toLocaleDateString("en-IN")}</td>
          <td className="p-4">{t.customer_mobile}</td>
          <td className="p-4 font-mono text-emerald-400">₹{(t.amount_paise / 100).toLocaleString("en-IN")}</td>
          <td className="p-4"><StatusBadge status={t.status} /></td>
          <td className="p-4"><button onClick={() => d.reverse(t.txn_id)} className="rounded-full border border-rose-500/40 px-3 py-1 text-xs text-rose-300 hover:bg-rose-500/10">Reverse</button></td>
        </tr>
      ))}
      {d.review.transactions.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">Nothing in review.</td></tr>}
    </Table>
  );

  if (tab === "redemptions") return (
    <Table testid="admin-redemptions-table" cols={["Date", "Customer", "Amount", "Method", "Status", ""]}>
      {d.reds.map((r) => (
        <tr key={r.redemption_id} className="border-t border-slate-800/60">
          <td className="p-4 text-slate-400">{new Date(r.created_at).toLocaleDateString("en-IN")}</td>
          <td className="p-4 font-mono text-xs">{r.customer_id}</td>
          <td className="p-4 font-mono">₹{(r.amount_paise / 100).toLocaleString("en-IN")}</td>
          <td className="p-4">{r.method}{r.method === "UPI" ? ` · ${r.dest || ""}` : r.bank?.account_number ? ` · ${r.bank.account_number} (${r.bank.ifsc || ""})` : ""}</td>
          <td className="p-4"><StatusBadge status={r.status} /></td>
          <td className="p-4">{r.status === "REQUESTED" && (
            <div className="flex gap-2">
              <button data-testid={`redeem-pay-${r.redemption_id}`} onClick={() => d.processRedeem(r.redemption_id, "PAID")} className="rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-slate-950">Mark paid</button>
              <button data-testid={`redeem-reject-${r.redemption_id}`} onClick={() => d.processRedeem(r.redemption_id, "REJECTED")} className="rounded-full border border-rose-500/40 px-3 py-1 text-xs text-rose-300">Reject</button>
            </div>
          )}</td>
        </tr>
      ))}
    </Table>
  );

  if (tab === "merchants") return d.selMerchant ? (
    <MerchantDetail merchantId={d.selMerchant} onBack={() => { d.setSelMerchant(null); d.loadAll(); }} />
  ) : (
    <Table testid="admin-merchants-table" cols={["Business", "Plan", "Tokens", "Subscription", "KYC", ""]}>
      {d.merchants.map((m) => (
        <tr key={m.merchant_id} data-testid={`merchant-row-${m.merchant_id}`} className="border-t border-slate-800/60">
          <td className="p-4">{m.business_name}</td>
          <td className="p-4 text-slate-400">{m.current_plan_id || "—"}</td>
          <td className="p-4 font-mono">{m.available_tokens}/{m.total_tokens}</td>
          <td className="p-4"><StatusBadge status={m.subscription_status === "ACTIVE" ? "ACTIVE" : "REQUESTED"} /></td>
          <td className="p-4"><StatusBadge status={m.kyc_status === "PENDING" ? "PENDING_KYC" : m.kyc_status} /></td>
          <td className="p-4">
            <button data-testid={`merchant-view-${m.merchant_id}`} onClick={() => d.setSelMerchant(m.merchant_id)} className="rounded-full border border-emerald-500/40 px-3 py-1 text-xs text-emerald-300 hover:bg-emerald-500/10">View</button>
          </td>
        </tr>
      ))}
    </Table>
  );

  if (tab === "kyc") return (
    <div className="space-y-4" data-testid="admin-kyc-list">
      {d.kycM.length === 0 && <div className="ledger-card rounded-2xl p-8 text-center text-slate-500">No KYC submissions yet.</div>}
      {d.kycM.map((m) => (
        <div key={m.merchant_id} data-testid={`kyc-merchant-${m.merchant_id}`} className="ledger-card rounded-2xl p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-heading text-lg font-bold">{m.business_name}</p>
              <p className="font-mono text-xs text-slate-500">{m.merchant_id}</p>
              {m.kyc_note && <p className="mt-1 text-xs text-rose-300">Note: {m.kyc_note}</p>}
            </div>
            <StatusBadge status={m.kyc_status === "PENDING" ? "PENDING_KYC" : m.kyc_status} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {m.documents.map((dd) => (
              <button key={dd.doc_id} data-testid={`kyc-view-${dd.doc_id}`} onClick={() => d.viewDoc(dd.doc_id)} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-xs hover:border-emerald-500/50">
                <FileText className="h-3.5 w-3.5 text-emerald-400" /> {KYC_LABELS[dd.doc_type] || dd.doc_type}
              </button>
            ))}
            {m.documents.length === 0 && <span className="text-xs text-slate-500">No documents.</span>}
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid={`kyc-approve-${m.merchant_id}`} onClick={() => d.reviewKyc(m.merchant_id, "APPROVED")} className="rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-slate-950 hover:bg-emerald-400">Approve</button>
            <button data-testid={`kyc-reject-${m.merchant_id}`} onClick={() => d.reviewKyc(m.merchant_id, "REJECTED")} className="rounded-full border border-rose-500/40 px-4 py-1.5 text-xs text-rose-300 hover:bg-rose-500/10">Reject</button>
          </div>
        </div>
      ))}
    </div>
  );

  if (tab === "campaign" && d.report) return (
    <div className="space-y-6" data-testid="admin-campaign-report">
      <div className="ledger-card rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Active campaign</p>
            <p className="font-heading text-lg font-bold">{d.report.campaignId}</p>
            <p className="text-xs text-slate-500">Tree {d.report.treeId} · {d.report.cycle_month} · {d.report.status}</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Subscriptions placed</p>
            <p data-testid="campaign-total-subs" className="font-heading text-2xl font-extrabold text-emerald-400">{d.report.total_subscriptions.toLocaleString("en-IN")} / {d.report.total_capacity.toLocaleString("en-IN")}</p>
          </div>
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MiniStat label="Total net (GST-excl)" value={d.report.totals.total_net_display} />
          <MiniStat label="L1-L20 allocation (60%)" value={d.report.totals.total_l1_l20_allocation_display} accent testid="campaign-total-allocation" />
          <MiniStat label="Remaining 40%" value={d.report.totals.remaining_40_display} />
          <MiniStat label="Available budget" value={d.report.totals.available_campaign_budget_display} accent />
        </div>
      </div>
      <div className="ledger-card overflow-hidden rounded-2xl">
        <p className="px-4 pt-4 text-xs text-slate-500">Per-Sub &amp; Full Level shown for a reference net of <span className="font-mono text-emerald-400">{d.report.reference_net_display}</span> (₹999 plan, GST-excl). Per-Sub = net × level%; Full Level = Per-Sub × capacity.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="campaign-report-table">
            <thead className="bg-slate-900/50 font-mono text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">Lvl</th><th className="p-3 text-right">Capacity</th><th className="p-3 text-right">Dist %</th>
                <th className="p-3 text-right">Subs</th><th className="p-3 text-right">Remaining</th>
                <th className="p-3 text-right">Level Alloc</th><th className="p-3 text-right">Per-Sub</th>
                <th className="p-3 text-right">Full Level</th><th className="p-3 text-right">Occupied</th><th className="p-3 text-right">Cumulative</th>
              </tr>
            </thead>
            <tbody>
              {d.report.levels.map((l) => (
                <tr key={l.level} className="border-t border-slate-800/60" data-testid={`campaign-level-row-${l.level}`}>
                  <td className="p-3 font-mono text-emerald-400">L{l.level}</td>
                  <td className="p-3 text-right font-mono">{l.capacity.toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono">{l.distribution_pct.toFixed(2)}%</td>
                  <td className="p-3 text-right font-mono">{l.current_subscriptions}</td>
                  <td className="p-3 text-right font-mono text-slate-400">{l.remaining_capacity.toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono text-emerald-400">₹{(l.level_allocation_paise / 100).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono text-slate-400">₹{(l.per_subscription_allocation_paise / 100).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono text-slate-500">₹{(l.full_level_allocation_paise / 100).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono text-slate-400">₹{(l.occupied_allocation_paise / 100).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono">₹{(l.cumulative_allocation_paise / 100).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (tab === "ledger") return (
    <Table testid="admin-budget-ledger-table" cols={["Date", "Type", "Amount", "Ref"]}>
      {d.ledger.map((e) => (
        <tr key={e.entry_id} className="border-t border-slate-800/60">
          <td className="p-4 text-slate-400">{new Date(e.created_at).toLocaleString("en-IN")}</td>
          <td className="p-4 font-mono text-xs">{e.type}</td>
          <td className={`p-4 font-mono ${e.amount_paise < 0 ? "text-rose-400" : "text-emerald-400"}`}>₹{(e.amount_paise / 100).toLocaleString("en-IN")}</td>
          <td className="p-4 font-mono text-xs text-slate-500">{e.ref || "—"}</td>
        </tr>
      ))}
    </Table>
  );

  if (tab === "audit") return (
    <Table testid="admin-audit-table" cols={["Date", "Actor", "Action", "Entity"]}>
      {d.audit.map((a) => (
        <tr key={a.audit_id} className="border-t border-slate-800/60">
          <td className="p-4 text-slate-400">{new Date(a.created_at).toLocaleString("en-IN")}</td>
          <td className="p-4 font-mono text-xs">{a.actor_id}</td>
          <td className="p-4 font-mono text-xs text-emerald-400">{a.action}</td>
          <td className="p-4 text-xs text-slate-400">{a.entity}:{a.entity_id}</td>
        </tr>
      ))}
    </Table>
  );

  if (tab === "config" && isSuper && d.cfg) return <ConfigTab d={d} />;
  if (tab === "tree" && isSuper && d.cfg) return <TreeTab d={d} />;
  if (tab === "roles" && isSuper) return <RolesTab d={d} />;

  return null;
}

function ConfigTab({ d }) {
  const { cfg, setCfg, saveConfig, busy } = d;
  const sum = (cfg.cashback_outcomes || []).reduce((s, o) => s + Number(o.weight_bps), 0);
  return (
    <div className="max-w-2xl space-y-6">
      <div className="ledger-card rounded-2xl p-8">
        <div className="flex items-center gap-2"><Settings className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-bold">Program configuration</h2></div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field label="GST %" value={cfg.gst_bps / 100} onChange={(v) => setCfg({ ...cfg, gst_bps: v * 100 })} testid="config-gst" />
          <Field label="Cashback % of purchase" value={cfg.cashback_bps / 100} onChange={(v) => setCfg({ ...cfg, cashback_bps: v * 100 })} testid="config-cashback" />
          <Field label="Hold period (days)" value={cfg.hold_period_days} onChange={(v) => setCfg({ ...cfg, hold_period_days: v })} testid="config-hold" />
          <Field label="Max cashback (₹)" value={cfg.max_cashback_paise / 100} onChange={(v) => setCfg({ ...cfg, max_cashback_paise: v * 100 })} testid="config-max" />
          <Field label="Min redemption (₹)" value={cfg.min_redeem_paise / 100} onChange={(v) => setCfg({ ...cfg, min_redeem_paise: v * 100 })} testid="config-minredeem" />
        </div>
        <div className="mt-6 rounded-lg bg-slate-900/60 p-4">
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Allowed denominations (₹)</p>
          <p className="mt-1 font-mono text-sm text-emerald-400">{cfg.denominations_paise.map((dn) => dn / 100).join(" · ")}</p>
        </div>
        <div className="mt-6 rounded-lg bg-slate-900/60 p-4" data-testid="config-outcomes">
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Cashback outcome probabilities</p>
          <p className="mt-1 text-xs text-slate-500">Backend-controlled weighted draw. Only ₹0 (Lucky Draw Ticket) / ₹20 / ₹50 / ₹100. Weights must total 100%.</p>
          <div className="mt-3 space-y-2">
            {(cfg.cashback_outcomes || []).map((o, i) => (
              <div key={o.amount_paise} className="flex items-center gap-3">
                <span className="w-44 font-mono text-sm text-slate-300">{o.amount_paise === 0 ? "🎟️ Lucky Draw Ticket" : `₹${o.amount_paise / 100} cashback`}</span>
                <input data-testid={`outcome-weight-${o.amount_paise}`} type="number" step="0.1" value={o.weight_bps / 100}
                  onChange={(e) => { const v = Math.round(Number(e.target.value) * 100); const arr = cfg.cashback_outcomes.map((x, j) => j === i ? { ...x, weight_bps: v } : x); setCfg({ ...cfg, cashback_outcomes: arr }); }}
                  className="w-28 rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm outline-none focus:border-emerald-500" />
                <span className="text-xs text-slate-500">%</span>
              </div>
            ))}
          </div>
          <p data-testid="config-outcomes-sum" className={`mt-2 font-mono text-xs ${sum === 10000 ? "text-emerald-400" : "text-rose-400"}`}>
            Total: {(sum / 100).toFixed(2)}%{sum === 10000 ? " ✓" : " (must be exactly 100%)"}
          </p>
        </div>
        <button data-testid="save-config-button" onClick={saveConfig} disabled={busy} className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-2.5 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save configuration
        </button>
      </div>

      <div className="ledger-card rounded-2xl border-rose-500/25 p-8" data-testid="danger-zone">
        <div className="flex items-center gap-2 text-rose-300"><AlertTriangle className="h-5 w-5" /><h2 className="font-heading text-xl font-bold">Danger zone</h2></div>
        <p className="mt-2 text-sm text-slate-400">Permanently delete all demo/test &amp; operational data — merchants, customers, tokens, subscriptions, transactions, lucky draws, audit logs and sessions. Program config, subscription plans and staff (admin) accounts are kept; a fresh campaign &amp; L1–L20 tree are recreated. Use this once on a new production database to remove migrated test data. This cannot be undone.</p>
        <button data-testid="reset-demo-data-button" onClick={d.resetDemoData} disabled={busy}
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-rose-500/40 px-6 py-2.5 font-semibold text-rose-300 hover:bg-rose-500/10 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Reset demo/test data
        </button>
      </div>
    </div>
  );
}

function TreeTab({ d }) {
  const { cfg, freeze } = d;
  return (
    <div className="space-y-6">
      <div className="ledger-card rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2"><GitBranch className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-bold">L1–L20 budget allocation tree</h2></div>
          <div className="flex items-center gap-4">
            <div data-testid="superadmin-tree-total-percentage" className="rounded-full bg-emerald-500/15 px-4 py-1.5 font-mono text-sm text-emerald-400">
              Sum: {(cfg.l1_l20.reduce((s, l) => s + Number(l.percent_bps), 0) / 100).toFixed(2)}% / {(Number(cfg.allocation_bps ?? 6000) / 100).toFixed(0)}%
            </div>
            <button data-testid="freeze-campaign-button" onClick={freeze} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-1.5 text-sm hover:border-sky-500/50"><Snowflake className="h-4 w-4" /> Freeze cycle</button>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">Internal accounting only. Does NOT decide customer cashback. <span className="font-mono text-amber-400">LOCKED</span> — capacities &amp; percentages are immutable.</p>
        <div className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {cfg.l1_l20.map((l) => (
            <div key={l.level} className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
              <p className="font-mono text-xs text-slate-500">L{l.level} · cap {l.capacity.toLocaleString("en-IN")}</p>
              <div className="mt-1 flex items-center gap-1">
                <input data-testid={`tree-level-${l.level}`} type="number" value={l.percent_bps / 100} readOnly disabled className="w-full cursor-not-allowed rounded border border-slate-800 bg-slate-950/60 px-2 py-1 text-sm text-slate-300 outline-none" />
                <span className="text-xs text-slate-500">%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RolesTab({ d }) {
  const { role, setRole, assignRole, users } = d;
  return (
    <div className="max-w-3xl space-y-6">
      <div className="ledger-card rounded-2xl p-6">
        <div className="flex items-center gap-2"><Users className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-bold">Assign role</h2></div>
        <div className="mt-4 flex flex-wrap gap-2">
          <input data-testid="role-email" placeholder="user email" value={role.email} onChange={(e) => setRole({ ...role, email: e.target.value })} className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
          <select data-testid="role-select" value={role.role} onChange={(e) => setRole({ ...role, role: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm">
            <option value="customer">customer</option><option value="merchant">merchant</option><option value="admin">admin</option><option value="super_admin">super_admin</option>
          </select>
          <button data-testid="assign-role-button" onClick={assignRole} className="rounded-full bg-emerald-500 px-5 font-semibold text-slate-950 hover:bg-emerald-400">Assign</button>
        </div>
      </div>
      <div className="ledger-card overflow-hidden rounded-2xl">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-4 text-left">Email</th><th className="p-4 text-left">Name</th><th className="p-4 text-left">Role</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-slate-800/60"><td className="p-4">{u.email}</td><td className="p-4 text-slate-400">{u.name}</td><td className="p-4 font-mono text-emerald-400">{u.role}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
